// apps/roleforge-app/src/components/deliverables/StarterSkillPack.tsx
// Plan 3b-ii Phase 7.4: Tool pills derived from recommendations, mapped via /api/skills/catalog.

import { useEffect, useState } from 'react';
import type { RecommendationRow, SkillCatalogEntry } from '../../lib/api-client';
import { fetchSkillCatalog } from '../../lib/api-client';

interface StarterSkillPackProps {
  recommendations: RecommendationRow[];
  jwt: string;
}

interface ToolItem {
  template_id: string;
  name: string;
  description: string;
}

function deduplicateTemplateIds(recommendations: RecommendationRow[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const rec of recommendations) {
    for (const tool of rec.tools) {
      const tid = typeof tool.template_id === 'string' ? tool.template_id : null;
      if (tid && !seen.has(tid)) {
        seen.add(tid);
        result.push(tid);
      }
    }
  }
  return result;
}

export function StarterSkillPack({ recommendations, jwt }: StarterSkillPackProps) {
  const [catalog, setCatalog] = useState<Map<string, SkillCatalogEntry>>(new Map());
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    void fetchSkillCatalog(jwt, 'tool')
      .then(({ entries }) => {
        setCatalog(new Map(entries.map((e) => [e.template_id, e])));
      })
      .catch(() => {
        setFetchFailed(true);
      });
  }, [jwt]);

  const templateIds = deduplicateTemplateIds(recommendations);

  if (templateIds.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic" data-testid="skill-pack-empty">
        Starter skills are being prepared from your blueprint.
      </p>
    );
  }

  const tools: ToolItem[] = templateIds.map((tid) => {
    const entry = catalog.get(tid);
    return {
      template_id: tid,
      name: entry?.name ?? tid,
      description: entry?.description ?? (fetchFailed ? 'Description unavailable.' : ''),
    };
  });

  return (
    <div className="flex flex-wrap gap-2" data-testid="skill-pack-pills">
      {tools.map((tool) => (
        <span
          key={tool.template_id}
          title={tool.description}
          className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-card px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-default hover:bg-slate-50 dark:bg-slate-900/40 transition-colors"
          data-testid={`skill-pill-${tool.template_id}`}
        >
          {tool.name}
        </span>
      ))}
    </div>
  );
}
